/**
 * Sticky header.
 * The pinning itself is CSS (see "Sticky header" in input.css) — it works with
 * JS off. This only adds the parts CSS can't do: the header's height for
 * scroll-padding, and the hide-on-scroll-down mode.
 *
 * Modes come from the header section's `sticky_header` setting, mirrored onto
 * the <header> as data-sticky:
 *   always    — pinned the whole time (CSS alone)
 *   scroll_up — slides out while scrolling down, returns on the way up
 *   none      — nothing to do
 */

const HIDDEN_CLASS = 'header-is-hidden';
// Ignore rubber-banding and sub-pixel jitter; only deliberate scrolls flip it.
const SCROLL_THRESHOLD = 5;

let controller = null;

function init() {
  controller?.abort();

  const header = document.querySelector('header[data-sticky]');
  const mode = header?.dataset.sticky;
  // Section groups add no markup of their own, so this is the header's own
  // section wrapper — a direct child of <body>, which is the element CSS pins.
  const root = header?.closest('.shopify-section') ?? header?.parentElement;

  root?.classList.remove(HIDDEN_CLASS);
  if (!header || !root || !mode || mode === 'none') {
    document.documentElement.style.removeProperty('--sticky-header-height');
    document.documentElement.style.removeProperty('--sticky-header-offset');
    return;
  }

  controller = new AbortController();
  const { signal } = controller;

  const measure = () => {
    const height = header.offsetHeight;
    const { style } = document.documentElement;
    style.setProperty('--sticky-header-height', `${height}px`);
    // Where the header's bottom edge currently sits, for anything that has to
    // stack under it (the PDP add-to-cart bar). Collapses to 0 while the header
    // is hidden, so the bar slides up with it instead of leaving a gap.
    style.setProperty('--sticky-header-offset', root.classList.contains(HIDDEN_CLASS) ? '0px' : `${height}px`);
  };

  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(header);
  signal.addEventListener('abort', () => observer.disconnect());

  if (mode !== 'scroll_up') return;

  let lastY = window.scrollY;
  let ticking = false;

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const delta = y - lastY;

    // A locked body means a drawer is open — leave the header where it is.
    if (y < 0 || Math.abs(delta) < SCROLL_THRESHOLD || document.body.style.overflow === 'hidden') {
      return;
    }

    // Only start hiding once the header has actually been scrolled past.
    root.classList.toggle(HIDDEN_CLASS, delta > 0 && y > root.offsetHeight);
    measure();
    lastY = y;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    },
    { passive: true, signal }
  );
}

init();

// The theme editor re-renders the section when the setting changes.
document.addEventListener('shopify:section:load', init);
